// Copyright ©️ 2026 eVolve MEP, LLC

import { useReducer } from 'react';

import type { TaskType, TaskTypeGroup } from '@evolvemep/api-ts';

import type { TaskTypeUpsertResponse } from 'helpers/helper-types';

export type SelectedTasksAction =
  | { type: 'toggle'; taskType: TaskType }
  | { type: 'selectAll'; taskTypes: TaskType[] }
  | { type: 'deselectAll'; taskTypes: TaskType[] }
  | { type: 'taskTypeUpdated'; taskType: TaskTypeUpsertResponse }
  | { type: 'taskTypeDeleted'; taskType: TaskType }
  | { type: 'taskTypeGroupDeleted'; taskTypeGroup: TaskTypeGroup };

const withoutTaskTypes = (selectedTasks: TaskType[], taskTypes: TaskType[]) => {
  const taskTypeIds = new Set(taskTypes.map((taskType) => taskType.taskTypeId));
  return selectedTasks.filter((taskType) => !taskTypeIds.has(taskType.taskTypeId));
};

const selectedTasksReducer = (selectedTasks: TaskType[], action: SelectedTasksAction): TaskType[] => {
  switch (action.type) {
    case 'toggle':
      return selectedTasks.some((taskType) => taskType.taskTypeId === action.taskType.taskTypeId)
        ? withoutTaskTypes(selectedTasks, [action.taskType])
        : [...selectedTasks, action.taskType];
    case 'selectAll':
      return [...withoutTaskTypes(selectedTasks, action.taskTypes), ...action.taskTypes];
    case 'deselectAll':
      return withoutTaskTypes(selectedTasks, action.taskTypes);
    case 'taskTypeUpdated':
      // TaskTypeUpsertResponse may not include every TaskType field, so merge onto the existing selection
      return selectedTasks.map((taskType) =>
        taskType.taskTypeId === action.taskType.taskTypeId ? { ...taskType, ...action.taskType } : taskType,
      );
    case 'taskTypeDeleted':
      return withoutTaskTypes(selectedTasks, [action.taskType]);
    case 'taskTypeGroupDeleted':
      return selectedTasks.map((taskType) =>
        taskType.taskTypeGroupId === action.taskTypeGroup.taskTypeGroupId
          ? { ...taskType, taskTypeGroupId: null }
          : taskType,
      );
    default:
      return selectedTasks;
  }
};

/** Tracks the task types selected in the task picker, keeping the selection in sync as task types and groups change. */
export const useSelectedTasks = () => useReducer(selectedTasksReducer, []);
